import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { JsonPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { PayoutsService } from '@/app/services/payouts/payouts.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { buildAirwallexConfirmationFilename } from '../../tabs/payouts/airwallex-confirmation-filename';

@Component({
  selector: 'ao-atom-airwallex-transaction-details',
  imports: [JsonPipe, TooltipDirective],
  templateUrl: './airwallex-transaction-details.html',
  styleUrl: './airwallex-transaction-details.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AirwallexTransactionDetails implements OnDestroy {
  @ViewChild('airwallexTransactionDetailsDialog', { static: false })
  airwallexTransactionDetailsDialog?: ElementRef<HTMLDialogElement>;

  attemptAirWallexId = input<string | undefined>();
  attemptId = input<string | undefined>();
  payoutId = input<string | undefined>();
  recipientFirstName = input<string | undefined>();
  recipientLastName = input<string | undefined>();
  payoutDate = input<Date | string | undefined>();

  private payoutsService = inject(PayoutsService);
  private toastNotificationService = inject(ToastNotificationService);
  private subscriptionList = new Subscription();

  airwallexTransfer = signal<unknown | undefined>(undefined);
  downloadingConfirmation = signal(false);

  dialogId = computed(() => `airwallex-transaction-details-${this.attemptId() ?? 'unknown'}`);
  titleId = computed(() => `${this.dialogId()}-title`);
  canDownloadConfirmation = computed(() => !!this.attemptAirWallexId());

  findTransactionDetails(): void {
    console.log('findTransactionDetails', this.attemptAirWallexId());
    this.airwallexTransfer.set(undefined);

    const transferId = this.attemptAirWallexId();
    if (transferId) {
      this.subscriptionList.add(
        this.payoutsService.getAirwallexTransferById(transferId).subscribe({
          next: (res) => this.airwallexTransfer.set(res),
          error: () => this.airwallexTransfer.set(undefined),
        }),
      );
    }

    this.airwallexTransactionDetailsDialog?.nativeElement?.showModal();
  }

  downloadConfirmationLetter(): void {
    const transferId = this.attemptAirWallexId();
    const payoutId = this.payoutId();
    if (!transferId || !payoutId || this.downloadingConfirmation()) {
      return;
    }

    this.downloadingConfirmation.set(true);
    this.subscriptionList.add(
      this.payoutsService.downloadAirwallexConfirmationLetter(transferId).subscribe({
        next: (blob) => {
          this.downloadingConfirmation.set(false);
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = buildAirwallexConfirmationFilename({
            transferId,
            payoutId,
            date: this.payoutDate(),
            firstName: this.recipientFirstName(),
            lastName: this.recipientLastName(),
          });
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.downloadingConfirmation.set(false);
          this.toastNotificationService.alert({
            title: 'Download failed',
            message: 'Could not download the Airwallex transfer confirmation.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  closeDrawer(): void {
    this.airwallexTransactionDetailsDialog?.nativeElement?.close();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
